import { useEffect, useState } from "react";
import { ClipboardList, Download, Send, Loader2, BookmarkPlus, FileSpreadsheet, Settings2, ShieldCheck, ShieldAlert, AlertTriangle, Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/lib/dialogs";
import { downloadBlob, downloadViaUrl, safeName } from "@/lib/download";

const API_HTTP = process.env.REACT_APP_BACKEND_URL || "";

const SUB_LEVELS = [
  { v: "L4-FPT", label: "L4 — Functional Performance Test" },
  { v: "L2-Checklist", label: "L2 — Prefunctional Checklist" },
  { v: "L3-Startup", label: "L3 — Startup Verification" },
  { v: "L1-Coordination", label: "L1 — Coordination Checklist" },
  { v: "L5-IST", label: "L5 — Integrated Systems Test" },
];

const DEFAULT_INFO = {
  template_name: "",
  equipment_type: "",
  canonical_level: 4,
  sub_level: "L4-FPT",
  tier: "custom",
  project_name: "",
  project_code: "",
  cxalloy_project_id: "",
  genelco_project_id: "",
};

const LEVEL_FROM_SUBLEVEL = { "L1-Coordination": 1, "L2-Checklist": 2, "L3-Startup": 3, "L4-FPT": 4, "L5-IST": 5 };

export default function CxAlloyPanel({ project, selectedDocs, onScriptSaved }) {
  const [info, setInfo] = useState({ ...DEFAULT_INFO });
  const [prompt, setPrompt] = useState("");
  const [spec, setSpec] = useState(null);
  const [validation, setValidation] = useState(null);
  const [working, setWorking] = useState(false);
  const [showSettings, setShowSettings] = useState(true);

  useEffect(() => {
    setInfo((i) => ({ ...i, project_name: i.project_name || project?.name || "" }));
  }, [project?.id, project?.name]);

  const setField = (k, v) =>
    setInfo((s) => {
      const next = { ...s, [k]: v };
      if (k === "sub_level" && LEVEL_FROM_SUBLEVEL[v]) next.canonical_level = LEVEL_FROM_SUBLEVEL[v];
      return next;
    });

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Describe the equipment/system to test");
      return;
    }
    setWorking(true);
    try {
      const res = await api.cxalloyGenerate({
        project_id: project.id,
        prompt,
        document_ids: selectedDocs,
        info,
      });
      setSpec(res.spec);
      setValidation(res.validation || null);
      setShowSettings(false);
      const v = res.validation?.summary;
      if (v?.errors) toast.warning(`Generated with ${v.errors} error${v.errors > 1 ? "s" : ""} — review the safety check before export`);
      else toast.success(`Generated ${res.spec.info.total_lines} items across ${res.spec.info.sections} sections${v?.warnings ? ` · ${v.warnings} warning${v.warnings > 1 ? "s" : ""}` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    } finally {
      setWorking(false);
    }
  };

  const download = async (format) => {
    if (!spec) return;
    const hasErrors = validation?.summary?.errors > 0;
    if (hasErrors) {
      const ok = await confirmDialog({
        title: "Export with validation errors?",
        message: `This template has ${validation.summary.errors} error(s) that may fail CxAlloy import. Auto-fix is usually safer.`,
        confirmLabel: "Download anyway",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      const { url, filename } = await api.cxalloyExportLink(spec, format, hasErrors);
      downloadViaUrl(url, filename);
      toast.success(`Downloading ${filename}`);
    } catch {
      toast.error("Download failed");
    }
  };

  const autofix = async () => {
    if (!spec) return;
    setWorking(true);
    try {
      const res = await api.cxalloyAutofix(spec);
      setSpec(res.spec);
      setValidation(res.validation);
      const n = (res.applied || []).length;
      toast.success(`Applied ${n} fix${n === 1 ? "" : "es"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed");
    } finally {
      setWorking(false);
    }
  };

  const save = async () => {
    if (!spec) return;
    const defaultName = spec?.info?.template_name || `CxAlloy ${new Date().toLocaleString()}`;
    const name = await promptDialog({
      title: "Save CxAlloy template to library",
      message: "You can iterate on it later in the Library tab.",
      defaultValue: defaultName,
      confirmLabel: "Save",
    });
    if (!name || !name.trim()) return;
    try {
      await api.createScript(project.id, {
        name: name.trim(),
        language: "cxalloy",
        code: JSON.stringify(spec),
        prompt,
        document_ids: selectedDocs,
      });
      toast.success(`Saved "${name.trim()}" to Library`);
      onScriptSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="cxalloy-panel">
      {/* Toolbar */}
      <div className="border-b border-[#2D7544] px-6 py-3 flex items-center gap-3 bg-[#050805]">
        <ClipboardList size={16} />
        <div className="dv-overline">CxAlloy Template Generator</div>
        <div className="font-mono text-[11px] text-[#66B07A] hidden md:block">
          {selectedDocs.length > 0
            ? `${selectedDocs.length} doc${selectedDocs.length === 1 ? "" : "s"} selected · ${info.sub_level}`
            : `using ALL project docs · ${info.sub_level}`}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="toggle-settings-btn"
            onClick={() => setShowSettings((s) => !s)}
            className="dv-btn-ghost flex items-center gap-1"
          >
            <Settings2 size={13} /> {showSettings ? "Hide metadata" : "Edit metadata"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5 bg-[#000000]">
        {/* Metadata */}
        {showSettings && (
          <div className="bg-[#050805] border border-[#2D7544] p-5" data-testid="cxalloy-meta">
            <div className="dv-overline mb-3">Template Metadata · written to Info sheet</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Labelled label="Template Name">
                <input data-testid="meta-template-name" className="dv-input w-full" value={info.template_name} onChange={(e) => setField("template_name", e.target.value)} placeholder="CH L4-FPT - Chiller Plant System" />
              </Labelled>
              <Labelled label="Equipment Type">
                <input data-testid="meta-equipment-type" className="dv-input w-full" value={info.equipment_type} onChange={(e) => setField("equipment_type", e.target.value)} placeholder="CH Chiller Plant System" />
              </Labelled>
              <Labelled label="Sub-Level">
                <select data-testid="meta-sub-level" className="dv-input w-full" value={info.sub_level} onChange={(e) => setField("sub_level", e.target.value)}>
                  {SUB_LEVELS.map((s) => (
                    <option key={s.v} value={s.v}>{s.label}</option>
                  ))}
                </select>
              </Labelled>
              <Labelled label="Tier">
                <select className="dv-input w-full" value={info.tier} onChange={(e) => setField("tier", e.target.value)}>
                  <option value="custom">custom</option>
                  <option value="standard">standard</option>
                </select>
              </Labelled>
              <Labelled label="Project Name">
                <input className="dv-input w-full" value={info.project_name} onChange={(e) => setField("project_name", e.target.value)} />
              </Labelled>
              <Labelled label="Project Code">
                <input className="dv-input w-full" value={info.project_code} onChange={(e) => setField("project_code", e.target.value)} placeholder="RNO-01" />
              </Labelled>
              <Labelled label="CxAlloy Project ID">
                <input className="dv-input w-full" value={info.cxalloy_project_id} onChange={(e) => setField("cxalloy_project_id", e.target.value)} placeholder="47186" />
              </Labelled>
              <Labelled label="Genelco Project ID">
                <input className="dv-input w-full" value={info.genelco_project_id} onChange={(e) => setField("genelco_project_id", e.target.value)} placeholder="25-0357" />
              </Labelled>
            </div>
          </div>
        )}

        {/* Result */}
        {spec ? (
          <SpecPreview spec={spec} validation={validation} onDownload={download} onSave={save} onAutofix={autofix} working={working} />
        ) : (
          <div className="border border-dashed border-[#2D7544] p-10 text-center bg-[#050805]">
            <FileSpreadsheet size={28} className="mx-auto mb-3" />
            <div className="font-heading text-3xl font-extrabold tracking-tighter">CxAlloy mode</div>
            <p className="text-sm text-[#66B07A] mt-3 max-w-xl mx-auto leading-relaxed">
              Generate a Template Builder–ready workbook (Info + Lines sheets, 15 columns, ' | '-delimited multi-values) from your project's specs, sequences of operation, and submittals. Edit the metadata, describe the equipment / system, and Claude Sonnet 4.5 builds the checklist or FPT.
            </p>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#2D7544] bg-[#050805] p-4 pr-44">
        <div className="flex gap-2 items-end">
          <textarea
            data-testid="cxalloy-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
            placeholder="e.g. Generate an L4-FPT for AHU-1 covering steady-state operation, smoke control, and failure modes per SOO §3.2."
            className="dv-input flex-1 resize-none min-h-[64px]"
          />
          <button
            data-testid="cxalloy-generate-btn"
            onClick={generate}
            disabled={working || !prompt.trim()}
            className="dv-btn-primary h-[64px] px-5 flex items-center gap-2"
          >
            {working ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Generate
          </button>
        </div>
      </div>
    </div>
  );
}

function Labelled({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="dv-overline">{label}</span>
      {children}
    </label>
  );
}

export function SpecPreview({ spec, validation, onDownload, onSave, onAutofix, working }) {
  const [showIssues, setShowIssues] = useState(false);
  const [links, setLinks] = useState({ xlsx: null, csv: null });
  const [linksError, setLinksError] = useState(null);
  const info = spec?.info || {};
  const lines = spec?.lines || [];
  const v = validation?.summary;
  const issuesByIndex = {};
  if (validation?.issues) {
    for (const it of validation.issues) {
      if (it.scope === "line" && typeof it.index === "number") {
        (issuesByIndex[it.index] = issuesByIndex[it.index] || []).push(it);
      }
    }
  }

  // Pre-fetch download URLs whenever the spec or its validation changes.
  // This lets us render real <a href> anchors as the XLSX/CSV buttons,
  // which work in browser sandboxes where programmatic .click() does not.
  useEffect(() => {
    let alive = true;
    if (!spec || !spec.lines?.length) {
      setLinks({ xlsx: null, csv: null });
      return;
    }
    const force = (validation?.summary?.errors || 0) > 0;
    setLinksError(null);
    Promise.all([
      api.cxalloyExportLink(spec, "xlsx", force).catch((e) => ({ _err: e })),
      api.cxalloyExportLink(spec, "csv", force).catch((e) => ({ _err: e })),
    ]).then(([x, c]) => {
      if (!alive) return;
      const errs = [x, c].filter((r) => r && r._err);
      if (errs.length) {
        setLinks({ xlsx: null, csv: null });
        setLinksError(errs[0]._err?.response?.data?.detail || "Could not prepare download");
      } else {
        setLinks({
          xlsx: { url: `${API_HTTP}${x.url}`, filename: x.filename },
          csv: { url: `${API_HTTP}${c.url}`, filename: c.filename },
        });
      }
    });
    return () => { alive = false; };
  }, [spec, validation]);

  const ValidationBadge = () => {
    if (!validation) return null;
    if (v.errors === 0 && v.warnings === 0) {
      return (
        <span data-testid="cxalloy-valid-badge" className="inline-flex items-center gap-1 px-2 py-1 border border-[#00FF41] text-[#00FF41] text-[10px] font-bold uppercase tracking-widest">
          <ShieldCheck size={12} /> Valid · ready to import
        </span>
      );
    }
    if (v.errors > 0) {
      return (
        <button
          data-testid="cxalloy-errors-badge"
          onClick={() => setShowIssues((s) => !s)}
          className="inline-flex items-center gap-1 px-2 py-1 border border-[#FF3344] text-[#FF3344] text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF3B30]/10"
        >
          <ShieldAlert size={12} /> {v.errors} error{v.errors > 1 ? "s" : ""}{v.warnings ? ` · ${v.warnings} warning${v.warnings > 1 ? "s" : ""}` : ""}
        </button>
      );
    }
    return (
      <button
        data-testid="cxalloy-warnings-badge"
        onClick={() => setShowIssues((s) => !s)}
        className="inline-flex items-center gap-1 px-2 py-1 border border-[#FFB100] text-[#FFB100] text-[10px] font-bold uppercase tracking-widest hover:bg-[#B47800]/10"
      >
        <AlertTriangle size={12} /> {v.warnings} warning{v.warnings > 1 ? "s" : ""}
      </button>
    );
  };

  return (
    <div className="bg-[#050805] border border-[#2D7544]" data-testid="cxalloy-preview">
      <div className="border-b border-[#2D7544] px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="dv-overline mb-1">Generated Template</div>
          <div className="font-heading text-xl font-extrabold tracking-tighter truncate">
            {info.template_name || "Untitled CxAlloy Template"}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A] mt-1 flex items-center gap-2 flex-wrap">
            <span>{info.sub_level} · {info.equipment_type || "no equipment type"} · {info.sections || 0} sections · {info.total_lines || 0} items</span>
            <ValidationBadge />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onAutofix && validation && (validation.summary.auto_fixable_errors || validation.summary.auto_fixable_warnings) ? (
            <button
              data-testid="cxalloy-autofix-btn"
              onClick={onAutofix}
              disabled={working}
              className="dv-btn-secondary flex items-center gap-1"
              title="Apply safe automatic fixes (renumber, drop empty, normalize types, fill missing name)"
            >
              <Wrench size={13} /> Auto-fix {validation.summary.auto_fixable_errors + validation.summary.auto_fixable_warnings}
            </button>
          ) : null}
          {onSave && (
            <button data-testid="cxalloy-save-btn" onClick={onSave} className="dv-btn-secondary flex items-center gap-1">
              <BookmarkPlus size={13} /> Save to Library
            </button>
          )}
          {links.xlsx ? (
            <a
              data-testid="cxalloy-download-xlsx"
              href={links.xlsx.url}
              download={links.xlsx.filename}
              rel="noopener"
              className="dv-btn-primary flex items-center gap-1 no-underline"
              onClick={() => toast.success(`Saving ${links.xlsx.filename}`)}
            >
              <Download size={13} /> XLSX
            </a>
          ) : (
            <button data-testid="cxalloy-download-xlsx" disabled className="dv-btn-primary flex items-center gap-1 opacity-50 cursor-wait">
              <Download size={13} /> XLSX
            </button>
          )}
          {links.csv ? (
            <a
              data-testid="cxalloy-download-csv"
              href={links.csv.url}
              download={links.csv.filename}
              rel="noopener"
              className="dv-btn-secondary flex items-center gap-1 no-underline"
              onClick={() => toast.success(`Saving ${links.csv.filename}`)}
            >
              <Download size={13} /> CSV
            </a>
          ) : (
            <button data-testid="cxalloy-download-csv" disabled className="dv-btn-secondary flex items-center gap-1 opacity-50 cursor-wait">
              <Download size={13} /> CSV
            </button>
          )}
        </div>
      </div>

      {(links.xlsx || links.csv) && (
        <div className="border-b border-[#2D7544] bg-[#000000] px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-[#3D8657] flex items-center gap-3 flex-wrap" data-testid="download-fallback-bar">
          <span>&gt; download not starting? right-click these and choose "save link as":</span>
          {links.xlsx && (
            <a
              data-testid="cxalloy-link-xlsx"
              href={links.xlsx.url}
              download={links.xlsx.filename}
              target="_blank"
              rel="noopener"
              className="text-[#00FF41] underline hover:text-[#00FFC9] normal-case tracking-normal"
            >
              {links.xlsx.filename}
            </a>
          )}
          {links.csv && (
            <a
              data-testid="cxalloy-link-csv"
              href={links.csv.url}
              download={links.csv.filename}
              target="_blank"
              rel="noopener"
              className="text-[#00FF41] underline hover:text-[#00FFC9] normal-case tracking-normal"
            >
              {links.csv.filename}
            </a>
          )}
        </div>
      )}
      {linksError && (
        <div className="border-b border-[#FF3344] bg-[#1A0606] px-5 py-2 text-[11px] text-[#FF3344]" data-testid="download-error">
          {String(linksError)}
        </div>
      )}

      {validation && (validation.issues?.length > 0) && (
        <div className="border-b border-[#2D7544]" data-testid="cxalloy-validation-panel">
          <button
            onClick={() => setShowIssues((s) => !s)}
            data-testid="toggle-issues-btn"
            className="w-full px-5 py-2 flex items-center justify-between bg-[#000000] hover:bg-[#0D1F12] text-left"
          >
            <div className="dv-overline flex items-center gap-2">
              {showIssues ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Import Safety Check · {validation.summary.errors} error{validation.summary.errors === 1 ? "" : "s"} · {validation.summary.warnings} warning{validation.summary.warnings === 1 ? "" : "s"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A]">
              {validation.summary.ok_to_export ? "OK TO EXPORT" : "BLOCKED — fix errors before export"}
            </div>
          </button>
          {showIssues && (
            <ul className="divide-y divide-[#1F4D2A]">
              {validation.issues.map((it, idx) => (
                <li
                  key={`issue-${idx}-${it.field||''}-${it.index??''}`}
                  data-testid={`issue-${idx}`}
                  className={`px-5 py-2 text-xs flex items-start gap-3 ${
                    it.severity === "error" ? "bg-[#1A0606]" : "bg-[#1A1505]"
                  }`}
                >
                  <span className={`font-mono text-[10px] uppercase tracking-widest shrink-0 ${
                    it.severity === "error" ? "text-[#FF3344]" : "text-[#FFB100]"
                  }`}>
                    {it.severity}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#00FF41]">{it.message}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A] mt-0.5">
                      {it.scope}{typeof it.index === "number" ? ` · row ${it.index + 1}` : ""}{it.field ? ` · ${it.field}` : ""}
                      {it.auto_fixable ? " · auto-fixable" : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#00FF41] text-[#000000] font-mono uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left w-16">#</th>
              <th className="px-3 py-2 text-left w-44">Type</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left w-48 hidden md:table-cell">Source</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const isHeader = l.line_type === "Header";
              const isInfo = l.line_type === "Information";
              const lineIssues = issuesByIndex[i] || [];
              const hasError = lineIssues.some((x) => x.severity === "error");
              const hasWarn = lineIssues.some((x) => x.severity === "warning");
              const rowBg = isHeader
                ? "bg-[#00FF41] text-[#000000]"
                : isInfo
                ? "bg-[#000000]"
                : "bg-[#050805]";
              const rowRing = hasError
                ? "ring-2 ring-inset ring-[#FF3344]"
                : hasWarn
                ? "ring-2 ring-inset ring-[#FFB100]"
                : "";
              return (
                <tr
                  key={`l-${i}-${(l.description||'').slice(0,16)}`}
                  data-testid={`cxalloy-line-${i}`}
                  className={`border-b border-[#EAEAEA] ${rowBg} ${rowRing}`}
                  title={lineIssues.map((x) => `[${x.severity}] ${x.message}`).join("\n") || undefined}
                >
                  <td className="px-3 py-2 font-mono text-[11px] align-top">
                    {l.line_number ?? "—"}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span className={`inline-block px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${
                      isHeader ? "border-white" : "border-[#2D7544]"
                    }`}>
                      {l.line_type}
                    </span>
                  </td>
                  <td className={`px-3 py-2 align-top leading-relaxed ${isHeader ? "font-heading font-extrabold tracking-tight" : ""}`}>
                    {l.description}
                    {hasError || hasWarn ? (
                      <div className={`mt-1 text-[10px] font-mono uppercase tracking-widest ${hasError ? "text-[#FF3344]" : "text-[#FFB100]"}`}>
                        {lineIssues[0].message}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 align-top font-mono text-[10px] uppercase tracking-widest text-[#66B07A] hidden md:table-cell">
                    {l.source || "—"}
                    {l.source_detail ? <div className="text-[#3D8657] normal-case tracking-normal">{l.source_detail}</div> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
